// Centralized normalization utilities (Phase 1 Unit 3)
// Contract:
//  - normalizeValue(component, value) returns possibly transformed value according to component.normalize
//  - Supported rules: trim, number, boolean, yn-01, yn-boolean, join-array, date-iso, date-parts-obj (pass-through)
//  - Unknown or missing rule => original value

export function coerceNumber(value) {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '');
    if (cleaned === '') return NaN;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : NaN;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : NaN;
}

function normalizeDateIso(value) {
  if (value === null || typeof value === 'undefined') return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!match) return value;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return value;
    if (month < 1 || month > 12) return value;
    const maxDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    if (day < 1 || day > maxDay) return value;
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const day = String(value.day || value.d || '').trim();
    const month = String(value.month || value.m || '').trim();
    const year = String(value.year || value.y || '').trim();
    if (!day && !month && !year) return value;
    return normalizeDateIso(`${year}-${month}-${day}`);
  }
  return value;
}

export function normalizeValue(component, value) {
  const rule = component?.normalize;
  if (!rule || rule === 'none') return value;
  try {
    switch (rule) {
      case 'trim':
        return typeof value === 'string' ? value.trim() : value;
      case 'number': {
        const n = coerceNumber(value);
        return Number.isFinite(n) ? n : value;
      }
      case 'boolean':
        if (value === 'true' || value === true) return true;
        if (value === 'false' || value === false) return false;
        return value;
      case 'yn-01':
        if (value === 'yes' || value === 'Yes' || value === true) return 1;
        if (value === 'no' || value === 'No' || value === false) return 0;
        return value;
      case 'yn-boolean':
        if (['yes','Yes',1,'1',true].includes(value)) return true;
        if (['no','No',0,'0',false].includes(value)) return false;
        return value;
      case 'join-array':
        return Array.isArray(value) ? value.join(', ') : value;
      case 'date-iso':
        return normalizeDateIso(value);
      case 'date-parts-obj':
        return value;
      default:
        return value;
    }
  } catch {
    return value;
  }
}

// Apply normalization across a component list and raw value map; returns new object (shallow for primitives)
export function normalizeAll(components, rawValues) {
  const out = {};
  components.forEach(c => {
    const key = (c && c.storageKey) ? c.storageKey : c?.id;
    if (!key) return;
    const raw = rawValues[key];
    if (raw !== undefined) out[key] = normalizeValue(c, raw);
  });
  return out;
}
